import { isContractProcedure } from "@orpc/contract";
import { readAccessMeta, readHeaderMeta, } from "./access.js";
import { admin } from "./admin.js";
import { apiKeys } from "./api-keys.js";
import { billing } from "./billing.js";
import { blocks } from "./blocks.js";
import { catalog } from "./catalog.js";
import { devtools, NEVER_STORED_INPUTS } from "./devtools.js";
import { health } from "./health.js";
import { identity } from "./identity.js";
import { mcpLibrary } from "./mcp-library.js";
import { projects } from "./projects.js";
import { runs } from "./runs.js";
/** Where the contract handler is mounted; the browser client and the server share it. */
export const API_PREFIX = "/api/v5";
// The whole API, declared once. Each server area contributes one object here; the server
// implements it (`implement(api)`) and the browser calls it through a typed client.
export const api = {
    health,
    identity,
    apiKeys,
    catalog,
    billing,
    devtools,
    admin,
    projects,
    runs,
    mcpLibrary,
    blocks,
};
/**
 * The `<area>.<name>` keys whose RPC explorer inputs may be stored: every procedure
 * except session-changing ones, the never-stored list, and the explorer's own area.
 * The server enforces it on write; the browser uses it to hide Save.
 */
export const STORABLE_INPUT_KEYS = new Set(Object.entries(api).flatMap(([area, branch]) => area === "devtools"
    ? []
    : Object.entries(branch).flatMap(([name, procedure]) => {
        if (!isContractProcedure(procedure))
            return [];
        const key = `${area}.${name}`;
        const meta = procedure["~orpc"].meta ?? {};
        return meta.sessionChanging === true || NEVER_STORED_INPUTS.includes(key) ? [] : [key];
    })));
/**
 * Every procedure by `<area>.<name>`, with the access its meta declares (null when it
 * declares none, which refuses it to keys). The key check reads the meta on the procedure
 * itself; this table is for whoever needs the whole list: the route check (which paths are
 * procedures) and the contract test (no procedure without a declaration).
 */
export const PROCEDURE_ACCESS = new Map(Object.entries(api).flatMap(([area, branch]) => Object.entries(branch).flatMap(([name, procedure]) => isContractProcedure(procedure)
    ? [[`${area}.${name}`, readAccessMeta(procedure["~orpc"].meta)]]
    : [])));
/**
 * Every procedure's request headers by `<area>.<name>`, as its meta declares them
 * (readHeaderMeta; empty for most): what the OpenAPI document lists as header parameters.
 */
export const PROCEDURE_HEADERS = new Map(Object.entries(api).flatMap(([area, branch]) => Object.entries(branch).flatMap(([name, procedure]) => isContractProcedure(procedure)
    ? [[`${area}.${name}`, readHeaderMeta(procedure["~orpc"].meta)]]
    : [])));
export * from "./access.js";
export * from "./admin.js";
export * from "./api-keys.js";
export * from "./billing.js";
export * from "./blocks.js";
export * from "./catalog.js";
export * from "./devtools.js";
export * from "./errors.js";
export * from "./health.js";
export * from "./idempotency.js";
export * from "./identity.js";
export * from "./limits.js";
export * from "./mcp-library.js";
export * from "./preflight.js";
export * from "./projects.js";
export * from "./routes.js";
export * from "./runs.js";
export * from "./validate.js";
export * from "./version.js";
//# sourceMappingURL=index.js.map